import { MILLISECONDS } from "shared/constants";
import {
  PLATFORM_LABELS,
  SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY,
  SocialPlatform,
  type SocialPlatformWithCharacterLimit,
} from "shared/constants/social";
import { isSameDayUTC } from "shared/helpers/date";
import type OrganisationSocialIntegration from "shared/resources/OrganisationSocialIntegration";

export const isSocialPlatformScheduleValid = ({
  scheduledAt,
  socialPlatform,
  tokenRefreshedAt: tokenRefreshedAtMs,
}: Pick<
  OrganisationSocialIntegration,
  "socialPlatform" | "scheduledAt" | "tokenRefreshedAt"
>) => {
  const platform = PLATFORM_LABELS[socialPlatform];
  if (!platform || !scheduledAt) return false;

  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];

  if (expiryDuration === Infinity) return true;

  const postScheduleDate = Date.parse(scheduledAt);
  const tokenRefreshedAt = Date.parse(tokenRefreshedAtMs);
  const tokenExpiresAt = tokenRefreshedAt + expiryDuration;

  if (isSameDayUTC(postScheduleDate, tokenExpiresAt)) {
    return false;
  }

  return postScheduleDate <= tokenExpiresAt;
};

export const isSocialPlatformTokenValid = ({
  expiredAt,
  expiresAt,
  socialPlatform,
  tokenRefreshedAt: tokenRefreshedAtMs,
}: Pick<
  OrganisationSocialIntegration,
  "socialPlatform" | "expiresAt" | "expiredAt" | "tokenRefreshedAt"
>) => {
  const platform = PLATFORM_LABELS[socialPlatform];

  if (!tokenRefreshedAtMs || !platform) return false;

  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];

  const now = Date.now();

  // expiredAt has higher priority because integration can expire at any time due to various reasons,
  // like users changing their password.
  // In the backend, we update this field with the current dateTime when an integration has expired.
  // And we set this field to null when the token is refreshed.
  if (expiredAt) return false;

  if (expiresAt) {
    // Twitter tokens never expire, but if expiresAt is explicitly set, respect it
    if (expiryDuration === Infinity) return true;

    return Date.parse(expiresAt) > now;
  }

  // Twitter tokens never expire when using legacy token age calculation
  if (expiryDuration === Infinity) return true;

  const tokenRefreshedAt = Date.parse(tokenRefreshedAtMs ?? "0");

  const tokenAge = now - tokenRefreshedAt;

  return tokenAge <= expiryDuration;
};

export const willSocialPlatformTokenExpireOnScheduledDay = ({
  scheduledAt,
  socialPlatform,
  tokenRefreshedAt,
}: Pick<
  OrganisationSocialIntegration,
  "socialPlatform" | "scheduledAt" | "tokenRefreshedAt"
>): boolean => {
  const platform = PLATFORM_LABELS[socialPlatform];
  if (!scheduledAt || !tokenRefreshedAt) return false;

  if (!platform) return false;

  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];

  if (expiryDuration === Infinity) return false; // Never expires

  const scheduledTime = Date.parse(scheduledAt);
  const tokenRefreshedTime = Date.parse(tokenRefreshedAt);
  const tokenExpiresAt = tokenRefreshedTime + expiryDuration;

  return isSameDayUTC(scheduledTime, tokenExpiresAt);
};

export const socialPlatformTokenProjectedExpiry = ({
  expiredAt,
  expiresAt,
  socialPlatform,
  tokenRefreshedAt,
}: Pick<
  OrganisationSocialIntegration,
  "socialPlatform" | "tokenRefreshedAt" | "expiredAt" | "expiresAt"
>) => {
  const platform = PLATFORM_LABELS[socialPlatform];
  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];

  // Twitter tokens never expire
  if (expiryDuration === Infinity) return Infinity;

  // expiredAt has higher priority because integration can expire at any time due to various reasons,
  // such as the user disconnecting their social media account. This is valid for all platforms.
  if (expiredAt) {
    return new Date(expiredAt);
  }

  // legacy behaviour
  if (expiresAt) return new Date(expiresAt);

  return new Date(Date.parse(tokenRefreshedAt) + expiryDuration);
};

export const getExpiresAtTimestamp = (
  platform: SocialPlatformWithCharacterLimit
) => {
  const expiryDuration = SOCIAL_ENGAGE_PLATFORM_TOKEN_EXPIRY[platform];

  if (expiryDuration === Infinity) return null;

  return new Date(Date.now() + expiryDuration).toISOString();
};

export const getValidUntilTimestamp = (
  platform: SocialPlatformWithCharacterLimit
) => {
  if (platform === "Tiktok") {
    return new Date(Date.now() + MILLISECONDS.day).toISOString();
  }

  return null;
};

export const buildIntegrationsLookup = <
  T extends Pick<OrganisationSocialIntegration, "remoteId">,
>(
  integrations: T[]
): Record<string, T> =>
  integrations.reduce(
    (obj, integration) =>
      Object.assign(obj, {
        [String(integration.remoteId)]: integration,
      }),
    {} as Record<string, T>
  );

/**
 * Maps a raw socialType string (as stored in the DB / sent by GPod) to the
 * corresponding SocialPlatform enum key used to index SOCIAL_ICONS.
 *
 * GPod sends "TikTok" (capital K) but SocialPlatform.TIKTOK is "Tiktok".
 * Rather than changing the enum — which would break the social engage OAuth
 * flow that also uses "Tiktok" via OAuthSocialPlatform — we normalise at the
 * point of icon lookup. Add further mappings here if other mismatches arise.
 */
export const toSocialIconKey = (socialType: string): string => {
  if (socialType === "TikTok") return SocialPlatform.TIKTOK;

  return socialType;
};
